import { Popover, PopoverPanel } from "@headlessui/react";
import React, { type FC, useCallback, useMemo } from "react";

import { useSessionDatePicker } from "../../hooks";
import { PopoverOpenSync } from "../shared/PopoverOpenSync";
import { SessionDatePicker } from "../shared/SessionDatePicker";
import {
  formatDateSelectionDisplay,
  getDatesFromSessionIds,
  getSessionIdsFromDates,
  MAX_SELECTABLE_SESSION_DATES,
} from "../shared/sessionDatePickerUtils";
import { SessionDatePopoverTrigger } from "../shared/SessionDatePopoverTrigger";

import type { Session } from "../../../../types";

interface SessionSelectStepProps {
  selectedSessionIds: string[];
  onSessionChange: (sessionIds: string[]) => void;
  sessions: Session[];
}

export const SessionSelectStep: FC<SessionSelectStepProps> = ({
  selectedSessionIds,
  onSessionChange,
  sessions,
}) => {
  const selectedDates = useMemo(
    () => getDatesFromSessionIds(selectedSessionIds, sessions),
    [selectedSessionIds, sessions],
  );

  const updateSessionsFromDates = useCallback(
    (dates: Date[]) => {
      const newSelectedSessionIds = getSessionIdsFromDates(dates, sessions);
      onSessionChange(newSelectedSessionIds);
    },
    [sessions, onSessionChange],
  );

  const {
    tempSelectedDates,
    setTempSelectedDates,
    setIsPopoverOpen,
    handleCancel,
    handleApply,
    handleClear,
  } = useSessionDatePicker({
    selectedDates,
    onApply: updateSessionsFromDates,
  });

  return (
    <div className="box-border content-stretch flex flex-col gap-[24px] items-center pb-0 pt-[12px] px-0 relative shrink-0 w-full max-w-[960px]">
      <p
        data-testid="reboot-live-session-select-prompt"
        className="font-medium leading-[23px] not-italic relative shrink-0 text-[18px] text-center text-white w-full"
      >
        Which session(s) would you like to analyze?
      </p>
      <div
        data-testid="reboot-live-session-select-container"
        className="bg-[#1d1d1f] relative rounded-[12px] shrink-0 w-[600px] overflow-visible"
      >
        <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col h-full items-start pb-[32px] pt-[32px] px-[32px] relative w-[600px]">
          <div className="w-full">
            <Popover>
              {({ open }) => (
                <>
                  <PopoverOpenSync
                    open={open}
                    onOpenChange={setIsPopoverOpen}
                  />
                  <SessionDatePopoverTrigger
                    variant="freeform"
                    data-testid="reboot-live-session-date-picker-button"
                    hasSelection={tempSelectedDates.length > 0}
                    displayText={formatDateSelectionDisplay(
                      tempSelectedDates,
                      "Select session date...",
                      5,
                    )}
                    onClear={handleClear}
                  />
                  <PopoverPanel className="absolute z-50 mt-2 left-1/2 -translate-x-1/2 w-[600px] bg-[#1d1d1f] rounded-md shadow-lg border-0 p-0">
                    {({ close }) => (
                      <SessionDatePicker
                        selectedDates={tempSelectedDates}
                        onDateChange={(dates) => {
                          setTempSelectedDates(dates);
                        }}
                        sessions={sessions}
                        maxSelectableDates={MAX_SELECTABLE_SESSION_DATES}
                        onCancel={() => {
                          handleCancel();
                          close();
                        }}
                        onApply={() => {
                          handleApply();
                          close();
                        }}
                      />
                    )}
                  </PopoverPanel>
                </>
              )}
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
};
